import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import {
  ArrowPathIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';
import {
  AssessmentDimension,
  QuickScoreResult,
  WorkflowRoute
} from './assessmentEngine';
import { trackBookingClick, trackContactClick } from '../../utils/analytics';

interface QuickScoreReportProps {
  workflowName: string;
  respondentRole?: string;
  result: QuickScoreResult;
  isSample?: boolean;
  onPrint: () => void;
  onRetake: () => void;
}

const PrintRules = createGlobalStyle`
  @media print {
    @page { size: Letter portrait; margin: 0; }

    html, body {
      width: 8.5in !important;
      min-height: 11in !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      background: white !important;
    }

    body * { visibility: hidden !important; }

    #quick-score-report,
    #quick-score-report * { visibility: visible !important; }

    #quick-score-report {
      position: absolute !important;
      inset: 0 auto auto 0 !important;
      width: 8.5in !important;
      margin: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    #quick-score-report .report-page {
      width: 8.5in !important;
      height: 11in !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0.48in 0.58in 0.42in !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      border-top: 10px solid var(--davinci-blue) !important;
      break-after: page;
      page-break-after: always;
      box-shadow: none !important;
    }

    #quick-score-report .report-page:last-child {
      break-after: auto;
      page-break-after: auto;
    }

    #quick-score-report .screen-only { display: none !important; }
    #quick-score-report .brand-rail { display: none !important; }

    #quick-score-report .report-page > h3 {
      margin-bottom: 8px !important;
      font-size: 24px !important;
      line-height: 1.12 !important;
    }

    #quick-score-report .section-intro {
      margin-bottom: 16px !important;
      font-size: 10.5px !important;
      line-height: 1.48 !important;
    }

    #quick-score-report .boundary-map { margin: 18px 0 22px !important; }
    #quick-score-report .boundary-step { padding: 17px 14px 4px 0 !important; }
    #quick-score-report .boundary-step strong { font-size: 10.5px !important; }
    #quick-score-report .boundary-step p { margin-top: 5px !important; font-size: 8.5px !important; line-height: 1.4 !important; }

    #quick-score-report .dimension-list { gap: 8px !important; margin-top: 12px !important; }
    #quick-score-report .dimension-row { gap: 14px !important; padding-bottom: 9px !important; }
    #quick-score-report .dimension-name span { font-size: 15px !important; }
    #quick-score-report .dimension-copy { font-size: 8.5px !important; line-height: 1.4 !important; }

    #quick-score-report .findings-grid { gap: 12px !important; margin-top: 16px !important; }
    #quick-score-report .finding-panel { padding: 15px !important; }
    #quick-score-report .finding-panel h4 { margin-bottom: 9px !important; font-size: 12px !important; }
    #quick-score-report .finding-panel li { margin-bottom: 5px !important; font-size: 8.5px !important; line-height: 1.4 !important; }

    #quick-score-report .next-step { margin-top: 17px !important; padding: 17px !important; }
    #quick-score-report .next-step h4 { margin-bottom: 7px !important; font-size: 15px !important; }
    #quick-score-report .next-step p { font-size: 10px !important; line-height: 1.48 !important; }

    #quick-score-report .method-grid { gap: 14px !important; margin-top: 17px !important; }
    #quick-score-report .method-card { padding: 13px !important; }
    #quick-score-report .method-card h4 { margin-bottom: 7px !important; font-size: 10.5px !important; }
    #quick-score-report .method-card p,
    #quick-score-report .method-card li { font-size: 7.8px !important; line-height: 1.42 !important; }

    #quick-score-report .upgrade { margin-top: 17px !important; padding: 15px !important; }
    #quick-score-report .upgrade h4 { margin-bottom: 6px !important; font-size: 12px !important; }
    #quick-score-report .upgrade p { font-size: 9px !important; line-height: 1.46 !important; }

    #quick-score-report .pair-headline { margin-top: 16px !important; padding: 13px 15px !important; }
    #quick-score-report .pair-headline strong { font-size: 11px !important; }
    #quick-score-report .pair-headline p { font-size: 9px !important; line-height: 1.46 !important; }
    #quick-score-report .checklist-intro { margin: 14px 0 8px !important; font-size: 8.5px !important; }
    #quick-score-report .checklist li { margin-bottom: 6px !important; font-size: 8.5px !important; line-height: 1.42 !important; }
  }
`;

const Shell = styled.section`
  width: min(1020px, 100%);
  margin: 0 auto;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ToolbarCopy = styled.div`
  strong {
    display: block;
    margin-bottom: 2px;
    color: var(--davinci-midnight);
    font-size: 0.94rem;
  }

  span {
    color: var(--davinci-grey);
    font-size: 0.82rem;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 460px) { flex-direction: column-reverse; }
`;

const ToolbarButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 10px 16px;
  border: 1px solid ${props => props.$primary ? 'var(--davinci-denim)' : 'var(--davinci-cloud)'};
  border-radius: 4px;
  background: ${props => props.$primary ? 'var(--davinci-denim)' : 'white'};
  color: ${props => props.$primary ? 'white' : 'var(--davinci-midnight)'};
  font: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;

  svg { width: 18px; }

  &:hover { border-color: var(--davinci-blue); }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.32); outline-offset: 2px; }
`;

const Report = styled.article`
  overflow: hidden;
  border: 1px solid var(--davinci-cloud);
  border-radius: 8px;
  background: white;
  box-shadow: 0 18px 48px rgba(20, 53, 98, 0.11);
`;

const ReportPage = styled.section`
  position: relative;
  min-height: 760px;
  padding: clamp(30px, 6vw, 64px);
  border-bottom: 1px solid var(--davinci-cloud);
  background: white;

  &:last-child { border-bottom: 0; }
`;

const BrandRail = styled.div`
  position: absolute;
  inset: 0 0 auto;
  height: 12px;
  background: var(--davinci-blue);
`;

const ReportHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 28px;
  padding-top: 18px;
  padding-bottom: 26px;
  border-bottom: 1px solid var(--davinci-cloud);

  img {
    width: 166px;
    height: auto;
  }

  @media (max-width: 560px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ReportMeta = styled.div`
  color: var(--davinci-stone);
  font-size: 0.74rem;
  line-height: 1.55;
  text-align: right;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  strong {
    display: block;
    color: var(--davinci-midnight);
  }

  @media (max-width: 560px) { text-align: left; }
`;

const ReportTitleBlock = styled.div`
  padding: clamp(44px, 8vw, 82px) 0 36px;

  p {
    margin: 0 0 14px;
    color: var(--davinci-denim);
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  h2 {
    max-width: 760px;
    margin: 0 0 20px;
    color: var(--davinci-midnight);
    font-size: clamp(2.35rem, 6vw, 4.9rem);
    line-height: 1.02;
    letter-spacing: -0.045em;
  }

  h3 {
    margin: 0;
    color: var(--davinci-grey);
    font-size: clamp(1.15rem, 2.4vw, 1.55rem);
    font-weight: 500;
    letter-spacing: 0;
  }
`;

const DecisionBand = styled.div`
  display: grid;
  grid-template-columns: 170px 1fr;
  border-top: 4px solid var(--davinci-blue);
  background: var(--davinci-steam, #f0f0f0);

  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const Grade = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 175px;
  background: var(--davinci-midnight);
  color: white;

  span {
    font-size: clamp(4.8rem, 12vw, 7.5rem);
    font-weight: 800;
    line-height: 0.8;
  }
`;

const Decision = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 175px;
  padding: 26px 30px;

  span {
    margin-bottom: 7px;
    color: var(--davinci-denim);
    font-size: 0.74rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  strong {
    color: var(--davinci-midnight);
    font-size: clamp(1.45rem, 3vw, 2.2rem);
    line-height: 1.2;
  }

  p {
    max-width: 680px;
    margin: 10px 0 0;
    color: var(--davinci-grey);
    font-size: 0.96rem;
    line-height: 1.55;
  }
`;

const ScoreGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 28px 0 0;
  border: 1px solid var(--davinci-cloud);

  div {
    min-height: 120px;
    padding: 20px;
    border-right: 1px solid var(--davinci-cloud);
  }

  div:last-child { border-right: 0; }

  dt {
    margin-bottom: 16px;
    color: var(--davinci-stone);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    color: var(--davinci-midnight);
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.2;
  }

  small {
    display: block;
    margin-top: 6px;
    color: var(--davinci-grey);
    font-size: 0.78rem;
    line-height: 1.4;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    div { min-height: auto; border-right: 0; border-bottom: 1px solid var(--davinci-cloud); }
    div:last-child { border-bottom: 0; }
  }
`;

const SectionLabel = styled.p`
  margin: 0 0 10px;
  color: var(--davinci-denim);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h3`
  max-width: 760px;
  margin: 0 0 14px;
  color: var(--davinci-midnight);
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  line-height: 1.14;
  letter-spacing: -0.035em;
`;

const SectionIntro = styled.p`
  max-width: 760px;
  margin: 0 0 30px;
  color: var(--davinci-grey);
  line-height: 1.65;
`;

const BoundaryMap = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 34px 0;
  border-top: 4px solid var(--davinci-blue);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    border-top: 0;
    border-left: 4px solid var(--davinci-blue);
  }
`;

const BoundaryStep = styled.div`
  position: relative;
  padding: 26px 20px 8px 0;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 0;
    width: 14px;
    height: 14px;
    border: 4px solid white;
    border-radius: 50%;
    background: var(--davinci-blue);
    box-shadow: 0 0 0 1px var(--davinci-blue);
  }

  span {
    display: block;
    margin-bottom: 7px;
    color: var(--davinci-denim);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    color: var(--davinci-midnight);
    font-size: 1rem;
    line-height: 1.35;
  }

  p {
    margin: 8px 0 0;
    color: var(--davinci-grey);
    font-size: 0.82rem;
    line-height: 1.5;
  }

  @media (max-width: 720px) {
    padding: 0 0 28px 24px;
    &::before { top: 2px; left: -11px; }
  }
`;

const DimensionList = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 30px;
`;

const DimensionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(145px, 0.72fr) minmax(220px, 1.35fr) minmax(220px, 1fr);
  gap: 22px;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--davinci-cloud);

  &:last-child { border-bottom: 0; }

  @media (max-width: 720px) { grid-template-columns: 1fr; gap: 8px; }
`;

const DimensionName = styled.div`
  strong { display: block; color: var(--davinci-midnight); }
  span { color: var(--davinci-denim); font-size: 1.35rem; font-weight: 800; }
`;

const ScoreTrack = styled.div<{ $score: number }>`
  height: 10px;
  overflow: hidden;
  background: var(--davinci-steam, #f0f0f0);

  &::after {
    content: '';
    display: block;
    width: ${props => props.$score}%;
    height: 100%;
    background: var(--davinci-blue);
  }
`;

const DimensionCopy = styled.p`
  margin: 0;
  color: var(--davinci-grey);
  font-size: 0.85rem;
  line-height: 1.5;
`;

const FindingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 34px;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const FindingPanel = styled.section<{ $priority?: boolean }>`
  padding: 26px;
  border-top: 4px solid ${props => props.$priority ? 'var(--davinci-denim)' : 'var(--davinci-blue)'};
  background: var(--davinci-steam, #f0f0f0);

  h4 {
    margin: 0 0 16px;
    color: var(--davinci-midnight);
    font-size: 1.08rem;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    margin: 0 0 12px;
    padding-left: 22px;
    color: var(--davinci-grey);
    font-size: 0.88rem;
    line-height: 1.5;
  }

  li:last-child { margin-bottom: 0; }

  li::before {
    content: '';
    position: absolute;
    top: 0.55em;
    left: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--davinci-blue);
  }
`;

const NextStep = styled.section`
  margin-top: 36px;
  padding: 30px;
  border-left: 5px solid var(--davinci-blue);
  background: var(--davinci-midnight);
  color: white;

  span {
    display: block;
    margin-bottom: 9px;
    color: var(--davinci-denim);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  h4 {
    margin: 0 0 12px;
    color: white;
    font-size: 1.4rem;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.82);
    line-height: 1.62;
  }
`;

const PairHeadline = styled.section`
  margin-top: 24px;
  padding: 20px 24px;
  border-left: 5px solid var(--davinci-denim);
  background: var(--davinci-steam, #f0f0f0);

  span {
    display: block;
    margin-bottom: 6px;
    color: var(--davinci-denim);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    color: var(--davinci-midnight);
    font-size: 1.12rem;
    line-height: 1.4;
  }

  p {
    margin: 8px 0 0;
    color: var(--davinci-grey);
    font-size: 0.9rem;
    line-height: 1.55;
  }
`;

const ChecklistIntro = styled.p`
  margin: 18px 0 10px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Checklist = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin: 0 0 10px;
    padding-left: 26px;
    color: rgba(255, 255, 255, 0.92);
    font-size: 0.9rem;
    line-height: 1.55;
  }

  li:last-child { margin-bottom: 0; }

  li::before {
    content: '';
    position: absolute;
    top: 0.32em;
    left: 0;
    width: 12px;
    height: 12px;
    border: 2px solid var(--davinci-blue);
    border-radius: 2px;
    background: transparent;
  }
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 34px;
  margin-top: 34px;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const MethodCard = styled.section`
  padding: 24px;
  border: 1px solid var(--davinci-cloud);

  h4 { margin: 0 0 12px; color: var(--davinci-midnight); font-size: 1rem; }
  p, li { color: var(--davinci-grey); font-size: 0.84rem; line-height: 1.56; }
  p { margin: 0; }
  ul { margin: 0; padding-left: 19px; }
`;

const Upgrade = styled.section`
  margin-top: 32px;
  padding: 28px;
  border-top: 4px solid var(--davinci-blue);
  background: var(--davinci-steam, #f0f0f0);

  h4 { margin: 0 0 10px; color: var(--davinci-midnight); font-size: 1.25rem; }
  p { margin: 0; color: var(--davinci-grey); line-height: 1.6; }
`;

const PageFooter = styled.footer`
  position: absolute;
  right: clamp(30px, 6vw, 64px);
  bottom: 22px;
  left: clamp(30px, 6vw, 64px);
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding-top: 12px;
  border-top: 1px solid var(--davinci-cloud);
  color: var(--davinci-stone);
  font-size: 0.68rem;
  letter-spacing: 0.04em;
`;

/*
 * On-screen next step. It sits outside #quick-score-report, which is the only
 * thing the print rules leave visible, and carries `screen-only` as well, so the
 * printed or saved PDF never contains it.
 */
const NextActions = styled.aside`
  margin-top: 22px;
  padding: clamp(20px, 4vw, 32px);
  border: 1px solid var(--davinci-cloud);
  border-radius: 8px;
  background: white;
  box-shadow: 0 12px 35px rgba(20, 53, 98, 0.08);

  h3 {
    margin: 0 0 6px;
    color: var(--davinci-midnight);
    font-size: 1.25rem;
  }

  p {
    margin: 0 0 18px;
    color: var(--davinci-grey);
    font-size: 0.92rem;
    line-height: 1.5;
  }

  @media print {
    display: none !important;
  }
`;

const NextActionLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const NextActionLink = styled.a<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 10px 18px;
  border: 1px solid ${props => props.$primary ? 'var(--davinci-denim)' : 'var(--davinci-cloud)'};
  border-radius: 4px;
  background: ${props => props.$primary ? 'var(--davinci-denim)' : 'white'};
  color: ${props => props.$primary ? 'white' : 'var(--davinci-midnight)'};
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;

  /* GlobalStyles sets a:hover { color: var(--davinci-denim) }, which beats the base
     color above and turned the primary button's label denim on denim. */
  &:hover,
  &:focus-visible {
    border-color: var(--davinci-blue);
    color: ${props => props.$primary ? 'white' : 'var(--davinci-midnight)'};
  }
  &:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.32); outline-offset: 2px; }
`;

const getBoundarySteps = (route: WorkflowRoute) => {
  if (route === 'Deterministic automation') {
    return [
      ['Input', 'Structured system data', 'Use validated fields and known sources.'],
      ['Rules engine', 'Code calculates or routes', 'Express the business logic directly in software.'],
      ['Control', 'Tests validate every result', 'Reject invalid data and log rule failures.'],
      ['Employee authority', 'People own exceptions', 'A named person resolves cases outside the rules.']
    ];
  }

  if (route === 'Human-led workflow') {
    return [
      ['Evidence', 'Context-rich source material', 'Preserve the information an employee needs to judge the case.'],
      ['Optional assist', 'AI researches or drafts', 'Keep assistance narrow, reviewable, and reversible.'],
      ['Control', 'Policy checks the draft', 'Use deterministic checks where requirements are explicit.'],
      ['Employee authority', 'People make the decision', 'The accountable role owns every consequential outcome.']
    ];
  }

  return [
    ['Evidence', 'Mixed workflow inputs', 'Preserve the original source and its context.'],
    ['AI prepares', 'Draft fields or analysis', 'Interpret the repeatable, unstructured portion of the work.'],
    ['Code validates', 'Required rules and fields', 'Use deterministic checks for known requirements.'],
    ['Employee authority', 'Confirm, correct, decide', 'People own exceptions and the final operating result.']
  ];
};

const dimensionOrder: AssessmentDimension[] = [
  'workflowFit',
  'humanControl',
  'executionReadiness',
  'learningReadiness'
];

const formatDate = () => new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
}).format(new Date());

const QuickScoreReport: React.FC<QuickScoreReportProps> = ({
  workflowName,
  respondentRole,
  result,
  isSample = false,
  onPrint,
  onRetake
}) => {
  const boundarySteps = getBoundarySteps(result.route);

  return (
    <Shell data-clarity-mask="True">
      <PrintRules />
      <Toolbar className="screen-only">
        <ToolbarCopy>
          <strong>{isSample ? 'Sample Quick Score report' : 'Your Quick Score report is ready'}</strong>
          <span>Review it now. Contact details are not required to save a copy.</span>
        </ToolbarCopy>
        <ToolbarActions>
          <ToolbarButton type="button" onClick={onRetake}>
            <ArrowPathIcon aria-hidden="true" />
            {isSample ? 'Take the assessment' : 'Retake'}
          </ToolbarButton>
          <ToolbarButton type="button" $primary onClick={onPrint}>
            <PrinterIcon aria-hidden="true" />
            Print or save PDF
          </ToolbarButton>
        </ToolbarActions>
      </Toolbar>

      <Report id="quick-score-report">
        <ReportPage className="report-page">
          <BrandRail className="brand-rail" />
          <ReportHeader>
            <img src="/images/logos/logo-horizontal-1x.png" alt="Davinci AI Solutions" />
            <ReportMeta>
              <strong>{isSample ? 'Illustrative sample' : 'Confidential snapshot'}</strong>
              Quick Score · Methodology 1.0<br />
              {formatDate()}
            </ReportMeta>
          </ReportHeader>

          <ReportTitleBlock>
            <p>AI Opportunity &amp; Readiness</p>
            <h2>Quick Score Report</h2>
            <h3>{workflowName}{respondentRole ? ` · ${respondentRole}` : ''}</h3>
          </ReportTitleBlock>

          <DecisionBand>
            <Grade aria-label={`Overall grade ${result.grade}`}><span>{result.grade}</span></Grade>
            <Decision>
              <span>Recommended route</span>
              <strong>{result.route}</strong>
              <p>{result.readinessLabel}. This grade summarizes opportunity and execution readiness; it is not a certification.</p>
            </Decision>
          </DecisionBand>

          <ScoreGrid>
            <div>
              <dt>Opportunity fit</dt>
              <dd>{result.opportunityFitScore}/100</dd>
              <small>Does this workflow suit AI assistance?</small>
            </div>
            <div>
              <dt>Execution readiness</dt>
              <dd>{result.executionReadinessScore}/100</dd>
              <small>Can your team pilot it responsibly?</small>
            </div>
            <div>
              <dt>Evidence confidence</dt>
              <dd>{result.confidence}</dd>
              <small>One respondent; claims are not independently verified.</small>
            </div>
          </ScoreGrid>

          <PageFooter>
            <span>Davinci AI Solutions · Practical AI, measurable advantage</span>
            <span>01</span>
          </PageFooter>
        </ReportPage>

        <ReportPage className="report-page">
          <BrandRail className="brand-rail" />
          <SectionLabel>Decision brief</SectionLabel>
          <SectionTitle>Put each part of the workflow in the right lane.</SectionTitle>
          <SectionIntro className="section-intro">{result.routeRationale}</SectionIntro>

          <BoundaryMap className="boundary-map" aria-label="Workflow Boundary Map">
            {boundarySteps.map(([label, title, copy]) => (
              <BoundaryStep className="boundary-step" key={label}>
                <span>{label}</span>
                <strong>{title}</strong>
                <p>{copy}</p>
              </BoundaryStep>
            ))}
          </BoundaryMap>

          <PairHeadline className="pair-headline">
            <span>Your strongest and weakest areas</span>
            <strong>
              Strongest: {result.strongestDimension.label} ({result.strongestDimension.score}). Weakest: {result.weakestDimension.label} ({result.weakestDimension.score}).
            </strong>
            <p>{result.gradeChangeLine}</p>
          </PairHeadline>

          <SectionLabel>Report card</SectionLabel>
          <SectionTitle>Four constructs, scored separately.</SectionTitle>
          <DimensionList className="dimension-list">
            {dimensionOrder.map((dimension) => {
              const score = result.dimensions[dimension];
              return (
                <DimensionRow className="dimension-row" key={dimension}>
                  <DimensionName className="dimension-name"><strong>{score.label}</strong><span>{score.score}</span></DimensionName>
                  <ScoreTrack $score={score.score} aria-label={`${score.label}: ${score.score} out of 100`} />
                  <DimensionCopy className="dimension-copy">{score.interpretation}</DimensionCopy>
                </DimensionRow>
              );
            })}
          </DimensionList>

          <PageFooter>
            <span>AI Opportunity &amp; Readiness Quick Score</span>
            <span>02</span>
          </PageFooter>
        </ReportPage>

        <ReportPage className="report-page">
          <BrandRail className="brand-rail" />
          <SectionLabel>Action plan</SectionLabel>
          <SectionTitle>Use the result to make one better decision.</SectionTitle>
          <SectionIntro className="section-intro">
            The Quick Score is designed to route the workflow—not to create a broad transformation plan from limited evidence.
          </SectionIntro>

          <FindingsGrid className="findings-grid">
            <FindingPanel className="finding-panel">
              <h4>What supports progress</h4>
              <ul>
                {(result.strengths.length ? result.strengths : ['No strong signal is confirmed yet. Use the priorities to strengthen the workflow before selecting technology.']).map((item) => <li key={item}>{item}</li>)}
              </ul>
            </FindingPanel>
            <FindingPanel className="finding-panel" $priority>
              <h4>What to resolve first</h4>
              <ul>
                {(result.priorities.length ? result.priorities : ['Preserve the current controls and verify them with representative evidence.']).map((item) => <li key={item}>{item}</li>)}
              </ul>
            </FindingPanel>
          </FindingsGrid>

          <NextStep className="next-step">
            <span>Recommended next step</span>
            <h4>Prove the workflow before scaling it.</h4>
            <p>{result.nextStep}</p>
            <ChecklistIntro className="checklist-intro">Evidence checklist — work through these before the next decision:</ChecklistIntro>
            <Checklist className="checklist">
              {result.evidenceChecklist.map((item) => <li key={item}>{item}</li>)}
            </Checklist>
          </NextStep>

          <MethodGrid className="method-grid">
            <MethodCard className="method-card">
              <h4>How the grade is calculated</h4>
              <p>
                The grade combines Workflow AI Fit (45%) with Execution Readiness (55%). Execution Readiness combines human control, practical delivery readiness, and the learning and governance loop.
              </p>
            </MethodCard>
            <MethodCard className="method-card">
              <h4>Important limitations</h4>
              <ul>{result.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
            </MethodCard>
          </MethodGrid>

          <Upgrade className="upgrade">
            <h4>When the decision needs more evidence</h4>
            <p>
              The Advanced Assessment explores your goals and twelve business areas, then prioritizes opportunities with evidence, cost assumptions, prerequisites and an action plan. It includes a private report and results-review call. The CAD $99 launch offer includes a $99 first-project credit, valid for 90 days; standard pricing is CAD $500. A valid sales code provides complimentary access without a project credit.
            </p>
            <p>
              Bring this checklist with you: the deep-dive reviews 2–3 business areas tied to this workflow, pressure-tests the evidence above, and leaves you with a costed action plan. Start the Advanced Assessment when every checklist item has an answer, even if the answer is “not yet proven”.
            </p>
          </Upgrade>

          <PageFooter>
            <span>
              <a
                href="mailto:info@davincisolutions.ai"
                style={{ color: 'inherit' }}
                onClick={() => trackContactClick('email', 'quick_score_report')}
              >
                info@davincisolutions.ai
              </a>
              {' · '}
              <a
                href="tel:+14032459429"
                style={{ color: 'inherit' }}
                onClick={() => trackContactClick('phone', 'quick_score_report')}
              >
                (403) 245-9429
              </a>
            </span>
            <span>03</span>
          </PageFooter>
        </ReportPage>
      </Report>

      <NextActions className="screen-only" aria-labelledby="quick-score-next-actions">
        <h3 id="quick-score-next-actions">What would you like to do with this result?</h3>
        <p>
          Walk through the report with a Davinci specialist, or go deeper with the Advanced
          Assessment for a costed action plan.
        </p>
        <NextActionLinks>
          <NextActionLink
            $primary
            href="/book"
            onClick={(event) => {
              // Let Cmd/Ctrl/Shift/Alt and middle clicks open elsewhere.
              if (event.button !== 0 || event.metaKey || event.ctrlKey
                  || event.shiftKey || event.altKey) return;
              // /book is a server 302 that unloads the page; hold the navigation
              // until the booking_click beacon is away.
              event.preventDefault();
              trackBookingClick('quick_score_report', () => window.location.assign('/book'));
            }}
          >
            Book a 30-minute results review
          </NextActionLink>
          <NextActionLink href="/assessment?tier=advanced">
            Start the Advanced Assessment
          </NextActionLink>
        </NextActionLinks>
      </NextActions>
    </Shell>
  );
};

export default QuickScoreReport;
